import React, { useState, useRef, useEffect, useCallback } from 'react';
import { API } from '../config';

async function smartParse(text) {
  try {
    const r = await fetch(`${API.apiBase}/api/parse-input`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text })
    });
    if (!r.ok) return {};
    return await r.json();
  } catch { return {}; }
}

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.max(1, Math.ceil(value / 25));
    const timer = setInterval(() => {
      start += step;
      if (start >= value) { setDisplay(value); clearInterval(timer); }
      else setDisplay(start);
    }, 30);
    return () => clearInterval(timer);
  }, [value]);
  return <>{display}</>;
}

const FEATURES = [
  { icon: '👥', label: '找老同学' },
  { icon: '📅', label: '班级活动' },
  { icon: '💬', label: '校友互动' },
  { icon: '🖼', label: '回忆分享' },
  { icon: '❤️', label: '聚会约起' },
];

export default function LandingPage({ users, needs, onRegister, onLogin }) {
  const [mode, setMode] = useState('smart');
  const [smartInput, setSmartInput] = useState('');
  const [parsed, setParsed] = useState(null);
  const [name, setName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [enrollYear, setEnrollYear] = useState('');
  const [gradYear, setGradYear] = useState('');
  const [className, setClassName] = useState('');
  const [headTeacher, setHeadTeacher] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showLoginPicker, setShowLoginPicker] = useState(false);
  const schoolRef = useRef(null);
  const suggRef = useRef(null);

  const searchSchools = useCallback(async (q) => {
    if (!q || q.length < 1) { setSuggestions([]); setShowSugg(false); setSearched(false); return; }
    try {
      const r = await fetch(`${API.apiBase}/api/schools/search?q=${encodeURIComponent(q)}`);
      if (!r.ok) return;
      const data = await r.json();
      setSuggestions(data);
      setSearched(true);
      setShowSugg(q.length >= 2);
    } catch {}
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchSchools(schoolName), 150);
    return () => clearTimeout(timer);
  }, [schoolName, searchSchools]);

  useEffect(() => {
    function handleClick(e) {
      if (suggRef.current && !suggRef.current.contains(e.target) && schoolRef.current && !schoolRef.current.contains(e.target))
        setShowSugg(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSmartSubmit() {
    const result = await smartParse(smartInput);
    setParsed(result);
    setSchoolName(result.schoolName || '');
    setEnrollYear(result.enrollYear || '');
    setGradYear(result.gradYear || '');
    setClassName(result.className || '');
    if (result.schoolName) setShowSugg(false);
  }

  function fillFormThenSubmit() {
    if (!parsed) return;
    if (!name.trim()) { alert('请填写姓名'); return; }
    handleSubmit({ preventDefault() {} });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !schoolName.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(API.users, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, schoolName, enrollYear, gradYear, className, headTeacher, phone, tags: ['校友'], preferences: ['校友互助'] })
      });
      if (!res.ok) { alert('API 错误: ' + res.status); setSubmitting(false); return; }
      const user = await res.json();
      const sameClass = users.filter(u => u.schoolName === user.schoolName && u.className === user.className).length;
      if (onRegister) onRegister(user, sameClass);
    } catch (err) { alert('网络错误: ' + err.message); }
    setSubmitting(false);
  }

  const connectedCount = needs.filter(n => n.status === 'connected').length;
  const otherUsers = users.filter(u => !['陈晴曦','景崎','姚悦琪'].includes(u.name)).slice(0, 8);

  return (
    <div style={{ width: '100%', maxWidth: 430, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', minHeight: '100vh', background: '#fff', position: 'relative', overflow: 'hidden' }}>
      {/* Background illustration */}
      <div style={{ position: 'absolute', right: -40, top: 80, fontSize: 180, opacity: 0.03, pointerEvents: 'none', lineHeight: 1, letterSpacing: -20, transform: 'rotate(8deg)', zIndex: 0, whiteSpace: 'nowrap' }}>🏫🌳✈️</div>

      {/* ===== Header ===== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px 0', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #e91e63, #ff6f91)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#fff' }}>👥</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>校友会 AI</div>
            <div style={{ fontSize: 9, color: '#ccc' }}>找回你的老同学</div>
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowLoginPicker(!showLoginPicker)}
            style={{ padding: '5px 16px', border: '1px solid #e91e63', borderRadius: 18, background: '#fff', color: '#e91e63', fontSize: 12, cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 13 }}>👤</span> 登录
          </button>
          {showLoginPicker && (
            <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', zIndex: 100, minWidth: 140, padding: 6, marginTop: 4 }}>
              {users.filter(u => !['陈晴曦','景崎','姚悦琪'].includes(u.name)).map(u => (
                <div key={u.id} onClick={() => { setShowLoginPicker(false); onLogin(u); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, hover: { background: '#fce4ec' } }}>
                  <span>👤</span> {u.name}
                  <span style={{ color: '#999', fontSize: 10, marginLeft: 'auto' }}>{u.schoolName?.slice(0, 8) || ''}</span>
                </div>
              ))}
              {users.filter(u => !['陈晴曦','景崎','姚悦琪'].includes(u.name)).length === 0 && (
                <div style={{ padding: '8px 12px', fontSize: 12, color: '#999' }}>暂无其他用户</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick login labels */}
      {users.filter(u => !['陈晴曦','景崎','姚悦琪'].includes(u.name)).length > 0 && (
        <div style={{ display: 'flex', gap: 6, padding: '6px 16px 0', position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: '#ccc', lineHeight: '22px' }}>已有账号：</span>
          {users.filter(u => !['陈晴曦','景崎','姚悦琪'].includes(u.name)).slice(0, 5).map(u => (
            <button key={u.id} onClick={() => onLogin(u)}
              style={{ padding: '2px 10px', border: 'none', borderRadius: 10, background: '#fce4ec', color: '#e91e63', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {u.name}
            </button>
          ))}
        </div>
      )}

      {/* ===== Hero ===== */}
      <div style={{ position: 'relative', zIndex: 1, margin: '14px 16px', height: 220, borderRadius: 28, background: 'linear-gradient(135deg, #fce4ec 0%, #f3e5f5 50%, #e3f2fd 100%)', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 24px' }}>
        <div style={{ position: 'absolute', right: 0, bottom: 0, fontSize: 140, opacity: 0.06, lineHeight: 1, letterSpacing: -10, pointerEvents: 'none' }}>🏫🌳✈️</div>
        <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: '#333', lineHeight: 1.2, position: 'relative' }}>
          <span style={{ color: '#e91e63' }}>AI</span> 帮你<br />
          找回青春同窗
        </h1>
        <p style={{ fontSize: 13, color: '#888', marginTop: 10, lineHeight: 1.5, position: 'relative' }}>
          输入母校信息，<br />智能匹配你的青春同窗
        </p>
      </div>

      {/* ===== Stats ===== */}
      <div style={{ display: 'flex', gap: 10, margin: '0 16px 14px', position: 'relative', zIndex: 1 }}>
        {[
          { value: users.length, label: '位校友已经回来', color: '#e91e63', bg: '#fce4ec' },
          { value: connectedCount, label: '段青春重新连接', color: '#7c4dff', bg: '#f3e5f5' },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '14px 0', background: s.bg, borderRadius: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: s.color, lineHeight: 1 }}>
              <AnimatedNumber value={s.value} />
            </div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ===== Feature Icons ===== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px', marginBottom: 14, position: 'relative', zIndex: 1 }}>
        {FEATURES.map((f, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 64, padding: '8px 0', background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>{f.icon}</div>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 500 }}>{f.label}</span>
          </div>
        ))}
      </div>

      {/* ===== Smart Input Card ===== */}
      <div style={{ position: 'relative', zIndex: 1, margin: '0 16px', background: '#fff', borderRadius: 20, padding: 16, boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginBottom: 2 }}>✨ 开始寻找老同学</div>
        <div style={{ fontSize: 11, color: '#bbb', marginBottom: 12 }}>一句话描述你的母校班级</div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 12, background: '#f8f4ff', borderRadius: 8, padding: 3 }}>
          <button onClick={() => setMode('smart')} style={{ flex: 1, padding: '5px 0', border: 'none', borderRadius: 6, background: mode === 'smart' ? '#7c4dff' : 'transparent', color: mode === 'smart' ? '#fff' : '#7c4dff', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
            AI 智能输入
          </button>
          <button onClick={() => setMode('manual')} style={{ flex: 1, padding: '5px 0', border: 'none', borderRadius: 6, background: mode === 'manual' ? '#7c4dff' : 'transparent', color: mode === 'manual' ? '#fff' : '#7c4dff', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
            逐项填写
          </button>
        </div>

        {mode === 'smart' ? (
          <div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={smartInput} onChange={e => setSmartInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSmartSubmit(); } }}
                placeholder="汕头二中 2028届 初三2班"
                style={{ flex: 1, padding: '10px 14px', border: '1px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', background: '#fafafa' }} />
              <button onClick={handleSmartSubmit} style={{ padding: '10px 16px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg, #7c4dff, #b388ff)', color: '#fff', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}>
                AI 解析
              </button>
            </div>
            {parsed && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#e8f5e9', borderRadius: 8, fontSize: 12, lineHeight: 1.6, color: '#2e7d32' }}>
                ✅ 识别成功：{parsed.schoolName || '未识别'} · {parsed.gradYear || '-'}届 · {parsed.className || '-'}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="你的姓名" style={{ padding: '10px 14px', border: '1px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', background: '#fafafa' }} />
            <div style={{ position: 'relative' }}>
              <input ref={schoolRef} value={schoolName} onChange={e => { setSchoolName(e.target.value); setShowSugg(true); }}
                onFocus={() => { if (suggestions.length > 0) setShowSugg(true); }}
                placeholder="母校名称" style={{ width: '100%', padding: '10px 14px', border: '1px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', background: '#fafafa', boxSizing: 'border-box' }} />
              {showSugg && (
                <div ref={suggRef} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #eee', borderRadius: 8, zIndex: 10, maxHeight: 140, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                  {suggestions.map(s => (
                    <div key={s.school_id} onClick={() => { setSchoolName(s.name); setShowSugg(false); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{s.name}</span>
                      <span style={{ color: '#999', marginLeft: 6, fontSize: 11 }}>{s.level} · {s.region}</span>
                    </div>
                  ))}
                  {searched && suggestions.length === 0 && schoolName.trim().length >= 2 && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#e91e63' }}>➕ 添加新学校「{schoolName}」</div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={enrollYear} onChange={e => setEnrollYear(e.target.value)} placeholder="入学年份" style={{ flex: 1, padding: '10px 14px', border: '1px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', background: '#fafafa' }} />
              <input value={gradYear} onChange={e => setGradYear(e.target.value)} placeholder="毕业年份" style={{ flex: 1, padding: '10px 14px', border: '1px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', background: '#fafafa' }} />
            </div>
            <input value={className} onChange={e => setClassName(e.target.value)} placeholder="班级（选填）" style={{ padding: '10px 14px', border: '1px solid #eee', borderRadius: 10, fontSize: 14, outline: 'none', background: '#fafafa' }} />
          </div>
        )}

        {/* Submit button */}
        <button onClick={() => {
          if (mode === 'smart' && parsed) {
            if (!name.trim()) { alert('请填写姓名'); return; }
            handleSubmit({ preventDefault() {} });
          } else if (mode === 'manual') {
            handleSubmit({ preventDefault() {} });
          } else {
            alert('请先点击 AI 解析识别信息');
          }
        }} disabled={submitting}
          style={{ width: '100%', padding: '13px', border: 'none', borderRadius: 12, background: 'linear-gradient(135deg, #e91e63, #ff6f91)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1, marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <span>✨</span> {submitting ? '加入中...' : '加入校友会'}
        </button>
      </div>

      {/* ===== Alumni avatars ===== */}
      {otherUsers.length > 0 && (
        <div style={{ margin: '16px 16px 14px', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#444' }}>已加入的校友</span>
          </div>
          <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 4 }}>
            {otherUsers.map((u, i) => (
              <div key={u.id || i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 44 }}>
                <div style={{ width: 40, height: 40, borderRadius: 20, background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, boxShadow: '0 2px 6px rgba(233,30,99,0.1)' }}>😊</div>
                <span style={{ fontSize: 10, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 44, textAlign: 'center' }}>{u.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Privacy footer ===== */}
      <div style={{ margin: '0 16px 24px', padding: '10px 14px', background: '#fce4ec', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, position: 'relative', zIndex: 1 }}>
        <span style={{ fontSize: 20 }}>🛡️</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#c62828' }}>隐私安全保障</div>
          <div style={{ fontSize: 10, color: '#888' }}>所有信息严格保密，仅用于校友匹配与互动</div>
        </div>
      </div>
    </div>
  );
}